import { useState } from "react";
import { brl } from "@/lib/api";
import { margemDoPreco, precoComMargem } from "@/lib/purchasePricing";
import { Badge, Button, Checkbox, Input, Select } from "@/components/ui-kit";
import ProductPicker from "@/components/panel/pickers/ProductPicker";
import { AlertCircle, Check, Sparkles } from "lucide-react";
import { VIA, pendencia, precisaAtencao, precoSugerido, margensInvertidas } from "./rows";
import ItemEconomics from "./ItemEconomics";

/**
 * Uma linha da NF-e e o seu destino no catálogo.
 *
 * O que mudou em relação à versão anterior, e por quê:
 *
 * - O destino era um `<select>` com TODAS as variações do catálogo achatadas,
 *   sem busca e em ordem de cadastro. Com algumas centenas de produtos, achar
 *   "Cadeado LT-20MM" era rolar uma lista que cobria a tela inteira. Agora é o
 *   mesmo ProductPicker do resto do sistema, com busca no servidor.
 *
 * - A tela carregava só 100 produtos e montava as opções a partir deles, mas o
 *   servidor casa contra o catálogo INTEIRO. Item casado fora dessas 100 tinha
 *   `variant_id` preenchido e nenhuma opção correspondente: o campo aparecia
 *   vazio e parecia que o vínculo tinha falhado. Com busca no servidor o
 *   problema deixa de existir, e o rótulo do casamento é mostrado à parte.
 *
 * - O casamento que o servidor achou (`matched_label`) era descartado. Agora
 *   aparece, junto de COMO ele foi feito — e o palpite por nome pede confirmação
 *   explícita, porque "LT-20MM" e "LT-25MM" são quase o mesmo texto.
 */
export default function ImportRow({ r, i, categorias, marcas = [], onChange, onRevisar }) {
  const item = r.name || `item ${i + 1}`;
  const falta = pendencia(r);
  const custoUnit = Number(r.unit_cost ?? r.unit_value) || 0;
  /* A margem que o preço DIGITADO representa sobre o custo novo — o campo de %
     é uma leitura do preço, não um estado à parte: dois estados para o mesmo
     número divergem assim que alguém mexe num deles. Vazio enquanto não há
     preço, para o campo não inventar uma margem que ninguém escolheu. */
  const margemDoPrecoLink = r.price > 0 && custoUnit > 0
    ? String(Math.round(margemDoPreco(custoUnit, r.price)))
    : "";
  /* O que está sendo digitado no campo de %, enquanto está sendo digitado.

     A leitura acima continua sendo a verdade — mas ela passa por um preço de
     duas casas, e reescrever o campo a cada tecla engolia o que a pessoa
     estava escrevendo: "2," nunca chegava a "2,5". Enquanto o campo tem foco
     vale o que foi digitado; ao sair, ele volta a ser a leitura do preço. */
  const [margemDigitada, setMargemDigitada] = useState(null);
  /* A margem que o preço de HOJE tem sobre o custo de HOJE. Vira o placeholder:
     mostra de onde o produto está partindo sem preencher nada. */
  const custoAtual = Number(r.current_cost) || custoUnit;
  const margemAtual = r.current_price > 0 && custoAtual > 0
    ? margemDoPreco(custoAtual, r.current_price)
    : null;
  const invertidas = margensInvertidas(r);
  const atencao = precisaAtencao(r);
  const via = r.via ? VIA[r.via] : null;

  return (
    <tr className={`border-b border-slate-100 last:border-0 align-top ${atencao ? "bg-amber-50/40" : ""}`}
      data-testid="stock-import-row">
      {/* Faixa de atenção na borda esquerda: numa nota longa é o que deixa
          localizar o que falta rolando, sem ler linha por linha. */}
      <td className={`px-4 py-3 border-l-2 ${atencao ? "border-amber-400" : "border-transparent"}`}>
        <p className="font-medium text-slate-900">{r.xml_name || r.name}</p>
        <p className="text-xs text-slate-400">
          cód {r.cProd}{r.ean ? ` · EAN ${r.ean}` : ""}
        </p>
        {/* A conta do item inteira, na ordem em que ela acontece. Antes era
            informação espalhada em três linhas cinzas que só apareciam quando
            havia frete — e some justamente quando alguém quer conferir se o
            custo bate com a nota. */}
        <ItemEconomics r={r} custoUnit={custoUnit} />

        {falta && (
          <p className="flex items-center gap-1 text-xs text-amber-700 mt-1" data-testid="stock-import-row-pending">
            <AlertCircle size={12} aria-hidden /> {falta}
          </p>
        )}
      </td>

      <td className="px-3 py-3 text-center">
        <Input type="number" min="0" size="sm" mono value={r.qty} onChange={(e) => onChange(i, "qty", e.target.value)}
          data-testid="stock-import-qty" aria-label={`Quantidade de ${item}`}
          className="w-16 text-center text-sm" />
      </td>

      <td className="px-3 py-3">
        <Select size="sm" value={r.action} onChange={(e) => onChange(i, "action", e.target.value)}
          data-testid="stock-import-action" aria-label={`Ação para ${item}`} className="w-auto text-sm">
          <option value="link">Vincular</option>
          <option value="create">Criar novo</option>
          <option value="skip">Ignorar</option>
        </Select>
      </td>

      <td className="px-4 py-3">
        {r.action === "link" && (
          <div className="space-y-2 min-w-[20rem]">
            {/* O que o servidor achou, e como. Fica ACIMA do seletor porque é a
                resposta que o operador precisa julgar — o seletor é só o que ele
                usa quando discorda. */}
            {via && r.matched_label && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={via.tone}>{via.label}</Badge>
                <span className="text-xs text-slate-600">{r.matched_label}</span>
                {r.via === "nome" && !r.revisado && (
                  <Button variant="secondary" size="sm" icon={Check} className="h-6 text-xs"
                    onClick={() => onRevisar(i)} data-testid="stock-import-confirm-guess">
                    Confere
                  </Button>
                )}
                {r.revisado && <Badge tone="success">conferido</Badge>}
              </div>
            )}
            {via?.hint && !r.revisado && (
              <p className="text-xs text-amber-700">{via.hint}</p>
            )}

            {/* Vincular deixou de ser só "somar estoque": o cadastro antigo,
                feito à mão antes de existir importação, ganha o que não tem.
                Dizer O QUE será tocado antes de confirmar é o que separa isso
                de mexer no cadastro em silêncio. */}
            {/* PREÇO DE VENDA no vínculo.
            
                Não existia: a importação atualizava custo e piso do produto e
                deixava o preço do ano passado. Uma compra mais cara entrava e a
                loja seguia vendendo pela margem antiga, sem ninguém perceber —
                e o único caminho era abrir o cadastro depois, item por item.
            
                Nasce VAZIO de propósito. Campo preenchido com o preço atual
                convidaria a confirmar sem pensar, e reprecificar em cima de uma
                nota mexe na etiqueta de produto que já está na prateleira. O
                valor de hoje aparece na ficha ao lado; aqui só se digita quando
                se quer mudar. */}
            <div className="flex flex-wrap items-end gap-2">
              {/* MARGEM no vínculo.

                  Só existia nas linhas "Criar novo", e depois da primeira
                  compra quase toda linha é vínculo — então na prática a
                  precificação por margem não existia para o caso mais comum.
                  Quem reprecifica não pensa em reais ("este vai a 182,90"),
                  pensa em percentual ("faço 70% em cima"); sem o campo, a conta
                  saía na calculadora, item por item, e o preço voltava para cá
                  digitado à mão.

                  Digitar aqui calcula o preço ao lado na hora; digitar o preço
                  recalcula a porcentagem. São a mesma informação por dois
                  caminhos, e a pessoa usa o que tiver na cabeça. */}
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`margem-link-${i}`}>
                  % sobre o custo
                </label>
                {/* `precoComMargem` e NÃO `precoSugerido`: o preço sugerido
                    arredonda para o ,90 da etiqueta, e num parafuso de R$ 4,79
                    o degrau entre 4,90 e 5,90 vale 21 pontos de margem. Como o
                    campo é a leitura desse preço, ele reescrevia o que acabara
                    de ser digitado — 1 virava 2, 3 virava 23, 70 virava 86. O
                    ,90 é convenção de ETIQUETA: vale onde o preço é sugerido e
                    aparece escrito, não onde a margem é a instrução. */}
                <Input id={`margem-link-${i}`} type="number" step="0.01" min="0" size="sm" mono
                  value={margemDigitada ?? margemDoPrecoLink}
                  onChange={(e) => {
                    setMargemDigitada(e.target.value);
                    onChange(i, "price", e.target.value === ""
                      ? ""
                      : precoComMargem(custoUnit, e.target.value));
                  }}
                  onBlur={() => setMargemDigitada(null)}
                  disabled={!(custoUnit > 0)}
                  placeholder={margemAtual != null ? String(Math.round(margemAtual)) : "—"}
                  aria-label={`Margem sobre o custo de ${item}`} className="w-24 text-sm"
                  data-testid="stock-import-link-margin" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`preco-link-${i}`}>
                  Novo preço de venda
                </label>
                <Input id={`preco-link-${i}`} type="number" step="0.01" min="0" size="sm" mono
                  value={r.price ?? ""} onChange={(e) => onChange(i, "price", e.target.value)}
                  placeholder={r.current_price != null ? brl(r.current_price) : "manter"}
                  aria-label={`Novo preço de venda de ${item}`} className="w-28 text-sm"
                  data-testid="stock-import-link-price" />
              </div>
              <p className="text-[11px] text-slate-400 pb-2">
                {r.price > 0
                  ? <>Vai passar de <b className="font-mono">{brl(r.current_price ?? 0)}</b> para <b className="font-mono text-slate-700">{brl(r.price)}</b>.</>
                  : "Em branco, o preço atual não é alterado."}
              </p>
              {/* Atalho para acompanhar o custo: aplica a mesma margem que o
                  preço de hoje tinha, agora sobre o custo novo. É a conta que a
                  pessoa faria na calculadora — e por isso também sem o ,90: o
                  botão promete MANTER a margem, e 50% sobre 4,79 arredondado
                  para 7,90 vira 65%. */}
              {custoUnit > 0 && r.current_price > custoUnit && !(r.price > 0) && (
                <Button variant="link" size="sm" icon={Sparkles} className="h-6 text-xs pb-1.5"
                  data-testid="stock-import-link-keep-margin"
                  onClick={() => onChange(i, "price", precoComMargem(custoUnit, margemDoPreco(r.current_cost || custoUnit, r.current_price)))}>
                  Manter a margem sobre o custo novo
                </Button>
              )}
            </div>

            {r.missing_fiscal?.length > 0 && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5"
                data-testid="stock-import-will-enrich">
                Vai completar no cadastro: <b>{r.missing_fiscal.join(", ").toUpperCase()}</b>
                {" "}— o que já estiver preenchido não é alterado.
              </p>
            )}

            <ProductPicker
              productId={r.product_id || ""}
              variantId={r.variant_id || ""}
              selectedLabel={r.matched_product_label}
              selectedVariantLabel={r.matched_variant_label}
              onChange={({ product, variant }) => onChange(i, "__pick", { product, variant })}
              placeholder="Buscar produto no catálogo…"
              testId="stock-import-variant"
            />

            {/* Marca no vínculo: ela SÓ entra se o produto estiver sem marca.
                Um cadastro que já tem "Mercur" não vira "MERCUR IND E COM"
                porque o distribuidor escreve assim na nota dele.
            
                Quando o produto JÁ TEM marca, isto deixa de ser campo e vira
                fato. Antes aparecia um input vazio — porque a marca exibida
                vinha do XML, e a NF-e quase nunca traz marca — e o vazio dava a
                entender que o cadastro estava incompleto, num produto que já
                tinha a marca preenchida desde a primeira importação. */}
            {r.current_brand ? (
              <p className="text-[11px] text-slate-500" data-testid="stock-import-brand-current">
                Marca <b className="text-slate-700">{r.current_brand}</b>
                <span className="text-slate-400"> · já no cadastro, não será alterada</span>
              </p>
            ) : (
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`marca-link-${i}`}>Marca</label>
                <Input id={`marca-link-${i}`} size="sm" value={r.brand || ""}
                  onChange={(e) => onChange(i, "brand", e.target.value)}
                  list={marcas.length ? "stock-import-marcas" : undefined}
                  placeholder="Ex.: Mercur" aria-label={`Marca de ${item}`}
                  className="w-full max-w-[18rem] text-sm" data-testid="stock-import-brand-link" />
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Este produto está sem marca — o que você digitar aqui completa o cadastro.
                </p>
              </div>
            )}
          </div>
        )}

        {r.action === "create" && (
          <div className="space-y-2">
            {/* Nome do CATÁLOGO, editável e já sem o lote que alguns
                fornecedores grudam na descrição ("... Lote: 0261/1124 ×2").
                Gravado cru, o produto entra no catálogo com número de lote no
                título e o balcão deixa de achá-lo pelo que ele é. */}
            <div>
              <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`nome-${i}`}>Nome no catálogo</label>
              <Input id={`nome-${i}`} size="sm" value={r.name}
                onChange={(e) => onChange(i, "name", e.target.value)}
                aria-label={`Nome no catálogo de ${item}`} className="w-full min-w-[18rem] text-sm"
                data-testid="stock-import-name" />
              {r.xml_name && r.xml_name !== r.name && (
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Na nota: {r.xml_name}
                </p>
              )}
            </div>
            {/* Marca. A NF-e não tem campo padrão para ela, então na maioria das
                notas isto chega vazio e é aqui que ela entra — uma vez só, na
                compra, em vez de numa segunda passada pelo cadastro que nunca
                acontece. O datalist sugere as marcas já usadas para "Mercur" e
                "MERCUR" não virarem duas. */}
            <div>
              <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`marca-${i}`}>Marca</label>
              <Input id={`marca-${i}`} size="sm" value={r.brand || ""}
                onChange={(e) => onChange(i, "brand", e.target.value)}
                list={marcas.length ? "stock-import-marcas" : undefined}
                placeholder="Ex.: Mercur" aria-label={`Marca de ${item}`}
                className="w-full max-w-[18rem] text-sm" data-testid="stock-import-brand" />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Select size="sm" value={r.category_slug} onChange={(e) => onChange(i, "category_slug", e.target.value)}
                data-testid="stock-import-category" aria-label={`Categoria de ${item}`} className="w-auto text-sm">
                <option value="">Categoria…</option>
                {categorias.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </Select>
              {/* Duas margens por item. A MÁXIMA gera o preço de etiqueta; a
                  MÍNIMA vira o piso de venda do produto, que o PDV passa a
                  respeitar no desconto. Editar qualquer uma recalcula só ESTA
                  linha. */}
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`mmin-${i}`}>% mín.</label>
                <Input id={`mmin-${i}`} type="number" step="0.01" min="0" size="sm" mono
                  value={r.margin_min_percentage ?? ""}
                  onChange={(e) => onChange(i, "margin_min_percentage", e.target.value)}
                  aria-label={`Margem mínima de ${item}`} aria-invalid={invertidas || undefined}
                  className={`w-20 text-sm ${invertidas ? "border-red-300" : ""}`}
                  data-testid="stock-import-margin-min-row" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`mmax-${i}`}>% máx.</label>
                <Input id={`mmax-${i}`} type="number" step="0.01" min="0" size="sm" mono
                  value={r.margin_max_percentage ?? ""}
                  onChange={(e) => onChange(i, "margin_max_percentage", e.target.value)}
                  aria-label={`Margem máxima de ${item}`} aria-invalid={invertidas || undefined}
                  className={`w-20 text-sm ${invertidas ? "border-red-300" : ""}`}
                  data-testid="stock-import-margin-max-row" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5" htmlFor={`preco-${i}`}>Preço de venda</label>
                <Input id={`preco-${i}`} type="number" step="0.01" size="sm" mono value={r.price}
                  onChange={(e) => onChange(i, "price", e.target.value)}
                  placeholder="0,00" aria-label={`Preço de venda de ${item}`} className="w-28 text-sm"
                  data-testid="stock-import-price" />
              </div>
            </div>

            {invertidas && (
              <p role="alert" className="text-xs text-red-600" data-testid="stock-import-margin-error">
                A margem mínima não pode passar da máxima — o piso ficaria acima do preço de etiqueta.
              </p>
            )}

            {/* Sugestão só aparece quando o preço ainda é o custo cru — ou seja,
                quando ninguém mexeu. Depois de editado, some. */}
            {Number(r.price) === custoUnit && custoUnit > 0 && (
              <Button variant="link" size="sm" icon={Sparkles} className="h-6 text-xs"
                onClick={() => onChange(i, "price", precoSugerido(custoUnit, 100))}
                data-testid="stock-import-suggest-price">
                Sugerir {brl(precoSugerido(custoUnit, 100))} (100% sobre o custo)
              </Button>
            )}

            {(r.ncm || r.icms_cst) && (
              <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="stock-import-taxes">
                <span className="font-semibold text-slate-600">Do XML:</span>{" "}
                {[
                  r.ncm && `NCM ${r.ncm}`,
                  r.cest && `CEST ${r.cest}`,
                  r.unidade && `un. ${r.unidade}`,
                  r.origem && `origem ${r.origem}`,
                  r.icms_cst && `ICMS ${r.icms_cst}${r.icms_aliquota ? ` (${r.icms_aliquota}%)` : ""}`,
                  r.pis_cst && `PIS ${r.pis_cst}`,
                  r.cofins_cst && `COFINS ${r.cofins_cst}`,
                ].filter(Boolean).join(" · ")}
                <span className="block text-amber-600 mt-0.5">
                  CFOP e CST vêm da nota do fornecedor — confirme com a contabilidade antes de emitir.
                </span>
              </p>
            )}
          </div>
        )}

        {r.action === "skip" && <span className="text-xs text-slate-400">Não será importado.</span>}

        {r.action !== "skip" && (
          <Checkbox
            checked={r.save_alias}
            onChange={(e) => onChange(i, "save_alias", e.target.checked)}
            label="lembrar este vínculo p/ próximas NF-e"
            aria-label={`Lembrar o vínculo de ${item} para próximas NF-e`}
            className="mt-2 text-xs text-slate-500"
          />
        )}
      </td>
    </tr>
  );
}
